import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { HtmlUtils } from '@shared/util/html-utils';
import { Injectable } from '@angular/core';
import { TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { TextSizeCheckUtils } from '@shared/util/text-size-check-utils';
import { SimpleTextValueVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { ConstraintCheckUtils } from './label-editor/constraint-check-validation/constraint-check-utils';
import { MetaElementUsageUtils } from '../../util/meta-element-usage-utils';

@Injectable({providedIn: 'root'})
export class UniformDirectPrintingService {

  constructor(
    protected readonly containerService: ContainerService,
    protected readonly textSizeCheckManager: TextSizeCheckManager,
  ) {
  }

  public getTextCheckConnections() {
    const articleContainers = this.containerService.enumerateArticleContainersInGlobal();
    const textureConnections = ArticleContainerUtils.getTextureConnections(articleContainers);
    const directTextCheckConnections = textureConnections.filter((connection) => ConnectionUtils.isDirectPrintingTextCheckConnection(connection));
    return directTextCheckConnections;
  }

  public async loadTextCheckInfos(textCheckConnections: Connection[]): Promise<boolean> {
    const textSizeCheckRequests = this.textSizeCheckManager.buildCommonTextSizeCheckRequest(textCheckConnections);
    const success = await this.textSizeCheckManager.retrieveTextSizeCheckInfos(textSizeCheckRequests);
    return success;
  }

  public async calculateUniformFontSize(textCheckConnections: Connection[]): Promise<number | undefined> {
    const textSizeCheckFontSizes: number[] = [];
    const manualFontSizes: number[] = [];
    for (const textCheckConnection of textCheckConnections) {
      const bestMatchingTextCheck = this.getFirstMatchingTextCheck(textCheckConnection);
      if (bestMatchingTextCheck) {
        textSizeCheckFontSizes.push(bestMatchingTextCheck.fontSize);
      }
      if (MetaElementUsageUtils.isManualCompressionTextureUsage(textCheckConnection.metaElementUsage)) {
        manualFontSizes.push(...HtmlUtils.extractLegacyFontSizeValues(textCheckConnection.metaElementUsage.textValue.text));
      }
    }
    if (manualFontSizes.length > 0) {
      return Math.max(...manualFontSizes);
    }
    if (textSizeCheckFontSizes.length > 0) {
      return Math.min(...textSizeCheckFontSizes);
    }
  }

  public async loadUniformFontSize(): Promise<number | undefined> {
    const textCheckConnections = this.getTextCheckConnections();
    await this.loadTextCheckInfos(textCheckConnections);
    return await this.calculateUniformFontSize(textCheckConnections);
  }

  /**
   * Selects the best matching text size check for the text of the given connection
   * (same selection as used by the label-editor, see TextSizeCheckUtils.getBestMatchingTextSizeCheck).
   */
  public getFirstMatchingTextCheck(textCheckConnection: Connection): TextSizeCheckVO | undefined {
    const textSizeChecks = this.textSizeCheckManager.getTextSizeCheckWithFallbackByConnection(textCheckConnection) ?? [];
    const textValue = textCheckConnection.metaElementUsage instanceof TextureAreaUsageVO
      ? textCheckConnection.metaElementUsage.textValue
      : undefined;
    const { fontBold, rotation, chars, lines } = textValue instanceof SimpleTextValueVO
      ? ConstraintCheckUtils.getTextCheckParametersFromTextValue(textValue)
      : { fontBold: 0, rotation: 0, chars: 1, lines: 1 };
    const bestMatch = TextSizeCheckUtils.getBestMatchingTextSizeCheck(textSizeChecks, {
      fontBold,
      rotation,
      chars: Math.max(1, chars),
      lines,
    });
    // no exact match for the given text: use the text check with the highest compression (like the label-editor without text parameters)
    return bestMatch ?? TextSizeCheckUtils.getBestMatchingTextSizeCheck(textSizeChecks);
  }
}
